<template>
  <div class="plan-feature-gate" :class="{ 'is-blocked': blocked }">
    <div class="plan-feature-gate-content">
      <slot />
    </div>
    <div
      v-if="blocked"
      class="plan-feature-gate-overlay"
      role="presentation"
    >
      <div class="plan-feature-gate-panel">
        <div class="plan-feature-gate-lock" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
            />
          </svg>
        </div>
        <p class="plan-feature-gate-title">{{ displayTitle }}</p>
        <p class="plan-feature-gate-desc">{{ displayDescription }}</p>
        <button type="button" class="plan-feature-gate-btn" @click="goUpgrade">
          {{ t('planGate.upgradeBtn') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps({
  blocked: { type: Boolean, default: true },
  title: { type: String, default: '' },
  description: { type: String, default: '' }
})

const displayTitle = computed(() => props.title || t('planGate.blockedTitle'))
const displayDescription = computed(() => props.description || t('planGate.blockedDescription'))

const router = useRouter()

const goUpgrade = () => router.push('/upgrade')
</script>

<style scoped>
.plan-feature-gate {
  position: relative;
  display: block;
}

/* Altura mínima para caber cadeado + textos + botão quando o slot é baixo */
.plan-feature-gate.is-blocked {
  min-height: 220px;
}

.plan-feature-gate-content {
  min-height: inherit;
}

.plan-feature-gate.is-blocked .plan-feature-gate-content {
  min-height: 220px;
  pointer-events: none;
  user-select: none;
  filter: blur(3px) saturate(0.85);
  opacity: 0.45;
}

.plan-feature-gate-overlay {
  position: absolute;
  inset: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 220px;
  padding: 20px 16px;
  box-sizing: border-box;
  border-radius: inherit;
  background: color-mix(in srgb, var(--bg-primary) 72%, rgba(15, 23, 42, 0.55));
  pointer-events: auto;
  overflow: hidden;
}

.plan-feature-gate-panel {
  text-align: center;
  padding: 4px 8px;
  max-width: 300px;
  width: 100%;
}

.plan-feature-gate-lock {
  width: 48px;
  height: 48px;
  margin: 0 auto 12px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: color-mix(in srgb, var(--accent-primary) 20%, var(--bg-secondary));
  border: 1px solid color-mix(in srgb, var(--accent-primary) 40%, var(--border-primary));
}

.plan-feature-gate-lock svg {
  width: 24px;
  height: 24px;
  color: var(--accent-primary);
}

.plan-feature-gate-title {
  margin: 0 0 6px;
  font-size: 15px;
  font-weight: 700;
  color: var(--text-primary);
}

.plan-feature-gate-desc {
  margin: 0 0 14px;
  font-size: 13px;
  line-height: 1.45;
  color: var(--text-secondary);
}

.plan-feature-gate-btn {
  border: none;
  border-radius: 4px;
  padding: 10px 18px;
  font-weight: 600;
  font-size: 14px;
  color: #fff;
  cursor: pointer;
  background: var(--accent-primary);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.plan-feature-gate-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px color-mix(in srgb, var(--accent-primary) 35%, transparent);
}
</style>
